import {
  getFromLocalStorage,
  saveToLocalStorage,
} from '@/common/utils/localStorage';

type TrackingIdentity = { userId: string; organisationId: string } | null;

const tracking: Record<'educator' | 'student', TrackingIdentity> = {
  educator: null,
  student: null,
};

export function identifyMixpanelUser(
  role: 'educator' | 'student',
  userId: string,
  organisationId: string,
) {
  tracking[role] = { userId, organisationId };
}

export function clearMixpanelUser(role: 'educator' | 'student') {
  tracking[role] = null;
}

/**
 * Tracks an event in Mixpanel
 * @param {string} event The name of the event to track
 * @param {Mixpanel.PropertyDict} data Additional properties to track with the event
 */
export default async function trackMixpanelEvent(
  event: string,
  data?: Record<string, unknown>,
): Promise<void> {
  // Skip events for dev
  const isDevServer = process.env.NODE_ENV === 'development';
  const isDeployPreview = window.location.hostname.includes('deploy-preview');
  if (isDevServer || isDeployPreview) {
    console.warn(
      `[trackMixpanelEvent] Skipping development mixpanel event: ${event}`,
    );
    return;
  }

  // If no current user, track as anon
  const currentlyTracking = tracking.student ?? tracking.educator ?? null;
  let trackingId = currentlyTracking?.userId;
  if (!trackingId) {
    let anonId = getFromLocalStorage('anonId');
    if (!anonId) {
      anonId = generateAnonId();
      saveToLocalStorage('anonId', anonId);
    }
    trackingId = anonId;
  }

  try {
    const response = await fetch(
      import.meta.env.VITE_FUNCTIONS_BASE_URL + '/trackMixpanelEvent',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: trackingId,
          organisationId: currentlyTracking?.organisationId,
          event,
          data: data || {},
        }),
      },
    );
    if (!response.ok) {
      console.error(
        '[trackMixpanelEvent] Error tracking event:',
        response.statusText,
      );
    }
  } catch (e) {
    console.error('[trackMixpanelEvent] Error tracking event', e);
  }
}

function generateAnonId() {
  const id = crypto.randomUUID().split('-').slice(0, 2).join('');
  return `anon-${id}`;
}
